import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";

import {
  ChatIntegration,
  ChatIntegrationSellerRelated,
  ChatIntegrationType,
  TagSocialMatching,
} from "models/chatIntegration";
import { AxiosError } from "axios";
import { messageToError, ValidationError } from "utils/validation";
import { toast } from "react-toastify";
import { getIntegrationSellers } from "services/chatIntegrationService";
import { SaveOrUpdateChatIntegrationFunction } from "../../viewModel";
import { IntegrationSeller } from "models/integrationSeller";
import { getSellers } from "services/sellerService";
import { Seller } from "models/seller";

export type BlipIntegrationForm = {
  type: ChatIntegrationType;
  name: string;
  blip: {
    host: string;
    sellers: string[];
    router?: string;
    key: string;
    analyticsToken?: string;
    department?: string;
    sellersRelated: ChatIntegrationSellerRelated[];
    tagsMatching: TagSocialMatching[];
  };
};

export const useViewModel = (
  selectedIntegration: ChatIntegration | undefined,
  handleSaveOrUpdate: SaveOrUpdateChatIntegrationFunction,
  provider: string
) => {
  const {
    register,
    formState: { errors },
    setValue,
    handleSubmit,
    setError,
    watch,
  } = useForm<BlipIntegrationForm>();

  const [step, setStep] = useState(0);
  const [loadingForm, setLoadingForm] = useState(false);
  const [sellers, setSellers] = useState<IntegrationSeller[]>([]);
  const [internalSellers, setInternalSellers] = useState<Seller[]>([]);

  const selectedSellers = watch("blip.sellers") ?? [];
  const selectedSellersRelated = watch("blip.sellersRelated") ?? [];

  const selectedSellersOptions = useMemo(() => {
    return selectedSellers.map(
      (seller) => sellers.find((_seller) => _seller.id === seller)!
    );
  }, [sellers, selectedSellers]);

  useEffect(() => {
    register("type", {
      required: { value: true, message: "O tipo de integração é obrigatório" },
    });
  }, [register]);

  useEffect(() => {
    setValue("name", selectedIntegration?.name ?? "");
    setValue("type", selectedIntegration?.type ?? "blip");
    setValue("blip.host", selectedIntegration?.blip?.host ?? "");
    setValue("blip.sellers", selectedIntegration?.blip?.sellers ?? []);
    setValue("blip.router", selectedIntegration?.blip?.router ?? "");
    setValue("blip.key", selectedIntegration?.blip?.key ?? "");
    setValue(
      "blip.analyticsToken",
      selectedIntegration?.blip?.analyticsToken ?? ""
    );
    setValue("blip.department", selectedIntegration?.blip?.department ?? "");
    setValue(
      "blip.sellersRelated",
      selectedIntegration?.blip?.sellersRelated ?? []
    );
    setValue("blip.tagsMatching", selectedIntegration?.blip?.tagsMatching ?? []);
  }, [selectedIntegration, setValue]);

  async function handleSubmitFormFirstStep(data: BlipIntegrationForm) {
    if (loadingForm) {
      return;
    }
    setLoadingForm(true);

    try {
      toast.info("Verificando integração...");
      const { attendents } = await getIntegrationSellers({
        type: data.type,
        blip: {
          host: data.blip.host,
          key: data.blip.key,
        },
      });
      setSellers(attendents);

      if (selectedIntegration && selectedIntegration.blip?.sellers) {
        const filteredSellers = selectedIntegration.blip.sellers.filter(
          (seller) => {
            return attendents.some((_seller) => _seller.id === seller);
          }
        );

        setValue("blip.sellers", filteredSellers);
      }
      toast.success("Integração verificada com sucesso!");
      setStep(1);
    } catch (error) {
      toast.error(errorMessage(error));
    }
    setLoadingForm(false);
  }

  const errorMessage = (error: unknown) => {
    const _err = error as AxiosError;
    if (_err.isAxiosError && _err.response?.data !== undefined) {
      const data = _err.response.data as {
        errors: ValidationError[] | undefined;
      };

      if (data.errors) {
        const fields = data.errors.map((error) => {
          setError(error.key as keyof BlipIntegrationForm, {
            message: messageToError(error.message),
          });
          return error.key;
        });
        return `Os campos ${fields.join(", ")} são inválidos`;
      }
      if (_err.response.status === 400)
        return "O domínio ou o token são inválidos";
    }
    return "Ocorreu um erro, por favor tente novamente";
  };

  async function handleSubmitFormSecondStep() {
    if (loadingForm) return;
    setLoadingForm(true);

    const internalSellers = await getSellers(1, 100, provider);
    setInternalSellers(internalSellers.data);

    setLoadingForm(false);

    setStep(2);
  }

  async function handleSubmitFormThirdStep(data: BlipIntegrationForm) {
    if (loadingForm) {
      return;
    }
    setLoadingForm(true);

    const valueOrUndefined = (value: string | undefined) => {
      if (value === undefined || value === null || value.trim() === "") {
        return undefined;
      }
      return value;
    };

    const router = valueOrUndefined(data.blip.router);
    const department = valueOrUndefined(data.blip.department);
    const analyticsToken = valueOrUndefined(data.blip.analyticsToken);

    await handleSaveOrUpdate({
      ...data,
      blip: {
        ...data.blip,
        router,
        department,
        analyticsToken,
        sellersRelated: data.blip.sellersRelated.filter((sellerRelated) =>
          data.blip.sellers.some(
            (seller) => seller === sellerRelated.externalSeller
          )
        ),
      },
    });

    setLoadingForm(false);
  }

  return {
    selectedIntegration,
    internalSellers,
    setValue,
    register,
    errors,
    loadingForm,
    handleSubmit,
    handleSubmitFormFirstStep,
    handleSubmitFormSecondStep,
    handleSubmitFormThirdStep,
    step,
    sellers,
    selectedSellers,
    selectedSellersOptions,
    selectedSellersRelated,
  };
};
